import { useTranslation } from "next-i18next";
import React from "react";
import styled from "styled-components";
import { device, size } from "../../constants";
import { useWindowSize } from "../../hooks/useWindowResize";
import { cn } from "../../lib/utils";
import { Button } from "../../pages/[locale]/casos/components/Button";
import { HighlightBar } from "./HighlightBar";

const Section = styled.section`
  width: 100%;
  --height-section-height: 791px;
  height: 100vh;
  min-height: var(--height-section-height);
  max-height: var(--height-section-height);
  background: ${({ theme }) => theme.colors["background-02"]};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: var(--height-section-height);
    background: linear-gradient(359.59deg, #15191E 7.44%, rgba(21, 25, 30, 0.526445) 45.15%, rgba(21, 25, 30, 0) 99.54%),
    linear-gradient(178.52deg, #15191E 16.74%, rgba(21, 25, 30, 0) 33.45%);
    z-index: 1;
  }

  @media ${device.laptopM} {
    --height-section-height: 688px;
    padding: 80px 112px;
    margin-top: 80px;

    &::before {
      left: 149px;
      background: linear-gradient(269.65deg, rgba(21, 25, 30, 0) 27.6%, rgba(21, 25, 30, 0.605248) 50.48%, #15191E 98.95%),
      linear-gradient(0deg, rgba(21, 25, 30, 0.15), rgba(21, 25, 30, 0.15));
    }
  }
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 115px;
  left: 0;
  width: 100%;
  height: var(--height-section-height);
  min-height: var(--height-section-height);
  max-height: var(--height-section-height);
  object-position: 66%;
  object-fit: cover;
  z-index: 0;

  @media ${device.laptopM} {
    top: 0;
    object-position: 150px -66px;
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 40px;
  margin-top: 88px;
  padding: 56px 24px;
  position: relative;
  z-index: 2;

  @media ${device.laptopM} {
    margin: 0;
    padding: 0;
  }
`;

const TextBlock = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  max-width: 1186px;
  margin: 0 auto;
`;

const Title = styled.h1`
  text-transform: uppercase;
  color: var(--product-color);
  font-weight: 700;
  margin: 16px 0 8px 0;
  font-size: 26px;
  line-height: 32px;
  letter-spacing: 0px;

  @media ${device.laptop} {
    font-size: 36px;
    line-height: 48px;
    margin-top: 20px;
    margin-bottom: 8px;
  }

  @media ${device.laptopM} {
    font-size: 48px;
    line-height: 64px;
    margin-top: 24px;
    margin-bottom: 8px;
  }
`;

const Subtitle = styled.h2`
  color: ${({ theme }) => theme.colors["neutral-01"]};
  font-weight: 500;
  font-size: 26px;
  line-height: 32px;
  letter-spacing: 0px;
  margin: 0;
  margin-bottom: 32px;
  text-transform: uppercase;
  width: 100%;

  @media ${device.laptop} {
    font-size: 36px;
    line-height: 48px;
  }

  @media ${device.laptopM} {
    font-size: 48px;
    line-height: 64px;
  }
`;

const HighlightText = styled.p`
  margin: 0;
  margin-top: 95px;
  font-weight: 400;
  font-size: 18px;
  line-height: 32px;
  letter-spacing: 0.9px;
  color: ${({ theme }) => theme.colors["neutral-01"]};
  max-width: 342px;

  strong {
    font-weight: 700;
    color: ${({ theme }) => theme.colors["neutral-01"]};
  }

  @media ${device.laptop} {
    font-size: 20px;
    line-height: 36px;
    letter-spacing: 1px;
    position: relative;
    margin-top: 28px;
    max-width: 844px;

    strong {
      font-size: 20px;
      line-height: 36px;
      letter-spacing: 1px;
    }
  }

  @media ${device.laptopM} {
    font-size: 22px;
    line-height: 40px;
    letter-spacing: 1.1px;
    position: relative;
    margin-top: 32px;

    strong {
      font-size: 22px;
      line-height: 40px;
      letter-spacing: 1.1px;
    }
  }
`;

export default function HeroSection({ title, subtitle, highlight, videoSrc }) {
	const { t } = useTranslation();
	const { width } = useWindowSize();
	const isLaptop = width >= size.laptop;

	return (
		<Section>
			<VideoBackground src={videoSrc} autoPlay loop muted playsInline />
			<Container className="">
				<TextBlock>
					<div className="bar">
						<HighlightBar />
					</div>
					<Title dangerouslySetInnerHTML={{ __html: title }} />
					<Subtitle dangerouslySetInnerHTML={{ __html: subtitle }} />
					<HighlightText dangerouslySetInnerHTML={{ __html: highlight }} />

					<Button
						as="button"
						className={cn(
							"mt-10 text-neutral-08 bg-vivid-04 uppercase laptop:max-w-[483px]",
							{
								"absolute bottom-0": !isLaptop,
							},
						)}
						onClick={() => {
							const contactForm = document.getElementById("contact-form");
							contactForm.scrollIntoView({
								behavior: "smooth",
								block: "center",
							});
						}}
					>
						{t("products.rpa.buttons.automateWithVerzel")}
					</Button>
				</TextBlock>
			</Container>
		</Section>
	);
}
